/**
 * The step-up SEAM — where an authenticated transport finds the page's second-factor prompt
 * (PLN-DEPT-TAB-005; the coordinator's ruling: every step-up-gated action must prompt in its web app,
 * and a gated call should prompt BY CONSTRUCTION rather than by per-screen wiring).
 *
 * The server seam (`@puffin-core/middleware` `createStepUpGate` → `requireMfaForAction`) answers
 * `403 MFA_REQUIRED { details.action }` when the tenant's MFA policy names an action and the session
 * token carries no live step-up. The census of the fleet's gated routes (105 registry rows, 210
 * routes, 162 web callers measured 2026-10-02) found the prompt wired on a handful of screens and
 * missing on the rest: a per-screen seam is a seam every new screen forgets. So the PROMPT lives here,
 * between the transport and the app's dialog:
 *
 *   - the app's MFA dialog (`@puffin-core/mfa-ui` `createMfaStepUp`'s provider) REGISTERS its
 *     `challenge(action)` on the page's seam while it is mounted;
 *   - every authenticated transport (`createApiClient` with `getToken`, and any transport that
 *     installs `installStepUpRetry`) asks the seam on a `MFA_REQUIRED` refusal and re-sends the
 *     request ONCE when the code is accepted.
 *
 * One prompt serves concurrent refusals: the step-up re-mints the SESSION token, so every request
 * waiting on it is satisfied by one accepted code — a save that fans out to five gated writes opens
 * the dialog once, not five times.
 *
 * @module @puffin-core/api-client/stepUpSeam
 */

/**
 * How a prompt says the person DISMISSED it — the ONE expected way a step-up ends without a code.
 * A handler rejects with it (minted by `seam.dismissed(message)`, so a registrant needs no import of
 * this package); the transport then answers the caller with the server's own refusal. Any other
 * rejection from a prompt is a defect and propagates as itself (SWALLOWED-CATCH-BURNDOWN-001 U3:
 * every rejection used to be read as a dismissal, so a crashed dialog surfaced as "verification
 * required").
 */
export class StepUpDismissedError extends Error {
  /** @param {string} [message] */
  constructor(message = 'The second-factor prompt was dismissed.') {
    super(message);
    this.name = 'StepUpDismissedError';
  }
}

/**
 * @returns {Readonly<{register: Function, isArmed: Function, request: Function, dismissed: Function}>}
 *   a seam
 */
export function createStepUpSeam() {
  let handler = null;
  let pending = null;

  return Object.freeze({
    /**
     * @param {(action: ?string, options: {enrollmentRequired: boolean}) => Promise<*>} next -
     *   resolves once the code is accepted (the session token re-minted), rejects with
     *   `this.dismissed(…)` when the person dismisses the prompt
     * @returns {Function} unregister — a no-op once a newer handler has replaced this one
     */
    register(next) {
      if (typeof next !== 'function') {
        throw new TypeError('stepUpSeam.register: the handler must be a function');
      }
      handler = next;
      return () => {
        if (handler === next) handler = null;
      };
    },

    /**
     * The rejection a handler answers a DISMISSED prompt with.
     *
     * @param {string} [message] - the registrant's own wording (callers may match on it)
     * @returns {StepUpDismissedError}
     */
    dismissed(message) {
      return new StepUpDismissedError(message);
    },

    /** @returns {boolean} whether a prompt is mounted to ask */
    isArmed() {
      return handler !== null;
    },

    /**
     * Ask the person for a fresh second factor — or join the prompt already open.
     *
     * @param {?string} action - the gated action the server named
     * @param {{enrollmentRequired?: boolean}} [options] - `enrollmentRequired`: the person holds no
     *   factor, so the prompt sends them to set one up instead of asking for a code
     * @returns {Promise<void>} settles with the prompt
     */
    request(action, { enrollmentRequired = false } = {}) {
      if (handler === null) {
        return Promise.reject(new Error('stepUpSeam: no second-factor prompt is mounted'));
      }
      if (pending === null) {
        const ask = handler;
        pending = Promise.resolve()
          .then(() => ask(action, { enrollmentRequired }))
          .then(() => undefined)
          .finally(() => {
            pending = null;
          });
      }
      return pending;
    },
  });
}

let pageSeam = null;

/**
 * The page's ONE seam — the default every authenticated transport consults and the one each app's
 * MFA dialog registers on (the `getPageActivityMonitor` precedent).
 *
 * @returns {ReturnType<typeof createStepUpSeam>}
 */
export function getPageStepUpSeam() {
  if (pageSeam === null) pageSeam = createStepUpSeam();
  return pageSeam;
}

/** The server's second-factor refusal code (`createStepUpGate`, MFA-STEPUP-FLEET). */
export const MFA_REQUIRED_CODE = 'MFA_REQUIRED';

/**
 * The server's refusal for a person who must step up but holds NO second factor
 * (`@puffin-core/middleware` `requireMfaForAction`, PERMISSIONS-CANON-001 S11 (b)). The seam asks
 * the same prompt, told to send the person to MFA SET-UP: no code can answer it, so it is never
 * re-sent.
 */
export const MFA_ENROLLMENT_REQUIRED_CODE = 'MFA_ENROLLMENT_REQUIRED';

/**
 * @param {*} error - an axios rejection
 * @returns {?{action: ?string, enrollmentRequired: boolean}} the denial, or null when this is no
 *   second-factor refusal
 */
function readSecondFactorDenial(error) {
  const body = error?.response?.data;
  const code = body?.error?.code;
  if (error?.response?.status !== 403) return null;
  if (code !== MFA_REQUIRED_CODE && code !== MFA_ENROLLMENT_REQUIRED_CODE) return null;
  return {
    action: body.error.details?.action ?? null,
    enrollmentRequired: code === MFA_ENROLLMENT_REQUIRED_CODE,
  };
}

/**
 * The ONE retry step, for any axios instance: on a second-factor refusal, ask the seam, then re-send
 * the request ONCE (the request interceptors run again, so the re-minted token is presented). A
 * dismissed prompt (`StepUpDismissedError`) — or a retry the server still refuses — rejects with
 * `reject(error)`, the transport's own rejection of the server's refusal, so the caller's ordinary
 * error path runs. A prompt that fails any other way is a defect, and it propagates as itself.
 *
 * @param {Object} params
 * @param {Function} params.client - the axios instance (callable with a request config)
 * @param {{isArmed: Function, request: Function}} params.stepUpSeam
 * @param {*} params.error - the rejection being handled
 * @param {(error: *) => Promise<never>} params.reject - the transport's rejection of a refusal
 * @returns {?Promise<*>} the retried request's settlement, or null when this is no step-up to take
 */
export function retryAfterStepUp({ client, stepUpSeam, error, reject }) {
  const denial = readSecondFactorDenial(error);
  const config = error?.config;
  if (!denial || !config || config._retryAfterStepUp || !stepUpSeam.isArmed()) return null;
  config._retryAfterStepUp = true;
  const aborted = () => new DOMException('The operation was aborted.', 'AbortError');
  const afterPrompt = () => {
    // An enrolment prompt cannot be satisfied in place (no factor answers it), so whatever it
    // settles with, the caller gets the server's own instructive refusal — never a re-send.
    if (denial.enrollmentRequired) return reject(error);
    return config.signal?.aborted ? Promise.reject(aborted()) : client(config);
  };
  return stepUpSeam.request(denial.action, { enrollmentRequired: denial.enrollmentRequired }).then(
    afterPrompt,
    (promptError) => {
      if (!(promptError instanceof StepUpDismissedError)) throw promptError;
      return reject(error);
    },
  );
}
